/**
 * Decide which baseline slot a just-saved baseline should land in.
 *
 * With one slot this was trivial — the save always took over the only overlay. With two, the
 * decision has to be made across slots rather than inside either one: if each slot independently
 * asked "am I free?", saving with both slots empty would load the same baseline into both.
 *
 * Precedence, in order:
 *   1. Every slot already showing this baseline is refreshed, so its curve picks up the new members
 *      (both slots when the user is comparing two configs of the same baseline).
 *   2. Otherwise the first empty slot takes it, which is what makes the save visible without
 *      disturbing a comparison already on screen.
 *   3. Otherwise slot 1 is replaced — with both slots full, something has to give, and slot 1 is
 *      the one the user is least likely to have set up as the reference.
 */

import type { Accessor } from 'solid-js';

/** The part of `useBaselineDisplay` this needs; the whole hook return is assignable. */
export interface BaselineSlotHandle {
  baselineId: Accessor<number | null>;
  refreshAfterSave: (id: number, name: string) => Promise<void>;
}

export async function showSavedBaselineInSlots(
  slots: BaselineSlotHandle[],
  baselineId: number,
  name: string
): Promise<void> {
  const alreadyShowing = slots.filter((s) => s.baselineId() === baselineId);
  if (alreadyShowing.length > 0) {
    for (const slot of alreadyShowing) {
      await slot.refreshAfterSave(baselineId, name);
    }
    return;
  }

  const target = slots.find((s) => s.baselineId() == null) ?? slots[0];
  if (!target) { return; }
  await target.refreshAfterSave(baselineId, name);
}
